import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CachedTokenContext } from "@seabrookstudios/auth";
import {
  GameById,
  GameRules,
  useCreateGameTopic,
  useGameRules,
  useGameTopics,
  useMyEntitlements,
  useUpdateRules,
} from "@seabrookstudios/pitch2table";
import { useQueryClient } from "@tanstack/react-query";
import endpoints from "../endpoints";
import {
  buildNestiFyzRulebookTopicBody,
  findExistingImportedRulebookTopic,
  getRulebookExportName,
  getRulebookIllustrationFiles,
  getRulebooksForImport,
  hasImportableRulebookContent,
} from "./rulebookImportUtils";
import { useSnackbar } from "notistack";
import { s2Url } from "@seabrookstudios/types";
import { usePreSignedUploadImage, useUploadImage } from "../users/queries";
import { GameImportMediaUpload } from "./GameImportMediaUpload";
import { getImportUploadSkipMessage, shouldSkipImportUpload } from "./importUploadUtils";

/**
 * @param {object} props
 * @param {() => void} props.onComplete
 */
const CompleteOnMount = ({ onComplete }) => {
  const hasCalled = useRef(false);

  useEffect(() => {
    if (hasCalled.current) {
      return;
    }

    hasCalled.current = true;
    onComplete();
  }, [onComplete]);

  return null;
};

/**
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook} rulebook
 * @returns {'pdf'|'topic'|'illustrations'|'done'}
 */
const getInitialRulebookPhase = (rulebook) => {
  if (rulebook.documentFile) {
    return "pdf";
  }

  if (hasImportableRulebookContent(rulebook)) {
    return "topic";
  }

  if (getRulebookIllustrationFiles(rulebook).length > 0) {
    return "illustrations";
  }

  return "done";
};

/**
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook} rulebook
 * @param {'pdf'|'topic'|'illustrations'|'done'} phase
 * @returns {'topic'|'illustrations'|'done'}
 */
const getNextRulebookPhase = (rulebook, phase) => {
  if (phase === "pdf") {
    if (hasImportableRulebookContent(rulebook)) {
      return "topic";
    }

    if (getRulebookIllustrationFiles(rulebook).length > 0) {
      return "illustrations";
    }

    return "done";
  }

  if (phase === "topic") {
    if (getRulebookIllustrationFiles(rulebook).length > 0) {
      return "illustrations";
    }

    return "done";
  }

  return "done";
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook} props.rulebook
 * @param {(index: number) => void} props.onIllustrationStart
 * @param {(index: number) => void} props.onIllustrationComplete
 * @param {(index: number) => void} props.onIllustrationSkip
 * @param {(index: number) => void} props.onIllustrationError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const UploadRulebookIllustrations = ({
  gameId,
  rulebook,
  onIllustrationStart,
  onIllustrationComplete,
  onIllustrationSkip,
  onIllustrationError,
  onComplete,
  onError,
}) => {
  const illustrationFiles = getRulebookIllustrationFiles(rulebook);

  if (illustrationFiles.length === 0) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  return (
    <GameImportMediaUpload
      gameId={gameId}
      files={illustrationFiles}
      onItemStart={onIllustrationStart}
      onItemComplete={onIllustrationComplete}
      onItemSkip={onIllustrationSkip}
      onItemError={onIllustrationError}
      onComplete={onComplete}
      onError={onError}
    />
  );
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook} props.rulebook
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const CreateImportedRulebookTopic = ({ gameId, rulebook, onStart, onComplete, onError }) => {
  const { data: topics = [] } = useGameTopics(gameId);
  const hasStarted = useRef(false);
  const createGameTopic = useCreateGameTopic(gameId);
  const { enqueueSnackbar } = useSnackbar();
  const topicName = getRulebookExportName(rulebook);

  useEffect(() => {
    if (hasStarted.current || !hasImportableRulebookContent(rulebook) || !topicName) {
      return;
    }

    hasStarted.current = true;
    onStart();

    const existingTopic = findExistingImportedRulebookTopic(topics, rulebook);

    if (existingTopic) {
      onComplete();
      return;
    }

    createGameTopic
      .mutateAsync({
        name: topicName,
        body: buildNestiFyzRulebookTopicBody(rulebook),
        type: "Rules",
        parent: undefined,
        parentId: undefined,
        communal: false,
        implementation: "Markdown",
      })
      .then(() => {
        enqueueSnackbar(`Rulebook topic "${topicName}" created`, { variant: "success" });
        onComplete();
      })
      .catch(() => {
        enqueueSnackbar("An error occurred importing rulebook topic", { variant: "error" });
        onError();
      });
  }, [createGameTopic, onComplete, onError, onStart, rulebook, topicName, topics, enqueueSnackbar]);

  return null;
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook} props.rulebook
 * @param {() => void} props.onTopicStart
 * @param {() => void} props.onTopicComplete
 * @param {() => void} props.onTopicError
 * @param {() => void} props.onComplete
 */
const FinalizeImportedRulebook = ({ gameId, rulebook, onTopicStart, onTopicComplete, onTopicError, onComplete }) => {
  if (!hasImportableRulebookContent(rulebook)) {
    return <CompleteOnMount onComplete={onComplete} />;
  }

  return (
    <CreateImportedRulebookTopic
      gameId={gameId}
      rulebook={rulebook}
      onStart={onTopicStart}
      onComplete={() => {
        onTopicComplete();
        onComplete();
      }}
      onError={onTopicError}
    />
  );
};

/**
 * @param {string|object} response
 * @returns {import('@seabrookstudios/pitch2table-core').AttachmentId|undefined}
 */
const parseAttachmentIdFromUploadResponse = (response) => {
  if (typeof response === "object" && response !== null) {
    const attachmentId = "id" in response ? response.id : undefined;
    return attachmentId;
  }

  if (typeof response !== "string" || !response) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(response);

    if (typeof parsed === "object" && parsed !== null) {
      return parsed.id;
    }

    return parsed;
  } catch {
    const attachmentId = /** @type {import('@seabrookstudios/pitch2table-core').AttachmentId} */ (response);
    return attachmentId;
  }
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('@seabrookstudios/pitch2table-core').AttachmentId} props.attachmentId
 * @param {string|undefined} props.name
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const RenameUploadedRulebook = ({ gameId, attachmentId, name, onComplete, onError }) => {
  const hasStarted = useRef(false);
  const updateRules = useUpdateRules(gameId, attachmentId);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;

    const renameRulebook = async () => {
      try {
        if (name) {
          await updateRules.mutateAsync({ deleted: undefined, name });
        }

        onComplete();
      } catch {
        onError();
      }
    };

    renameRulebook();
  }, [name, onComplete, onError, updateRules]);

  return null;
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook} props.rulebook
 * @param {() => void} [props.onPdfComplete]
 * @param {() => void} [props.onPdfSkip]
 * @param {() => void} props.onTopicStart
 * @param {() => void} props.onTopicComplete
 * @param {() => void} props.onTopicError
 * @param {(index: number) => void} props.onIllustrationStart
 * @param {(index: number) => void} props.onIllustrationComplete
 * @param {(index: number) => void} props.onIllustrationSkip
 * @param {(index: number) => void} props.onIllustrationError
 * @param {() => void} props.onStart
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
const UploadRulebook = ({
  gameId,
  rulebook,
  onPdfComplete,
  onPdfSkip,
  onTopicStart,
  onTopicComplete,
  onTopicError,
  onIllustrationStart,
  onIllustrationComplete,
  onIllustrationSkip,
  onIllustrationError,
  onStart,
  onComplete,
  onError,
}) => {
  const { data: rules = [] } = useGameRules(gameId);
  const { data: entitlements, isLoading: isEntitlementsLoading } = useMyEntitlements();
  const hasLargeFileUploads = Boolean(entitlements?.hasLargeFileUploads);
  const [attachmentId, setAttachmentId] = useState(
    /** @type {import('@seabrookstudios/pitch2table-core').AttachmentId|null} */ (null)
  );
  const [phase, setPhase] = useState(() => getInitialRulebookPhase(rulebook));
  const hasStarted = useRef(false);
  const hasCompleted = useRef(false);
  const exportName = getRulebookExportName(rulebook);

  const advancePhase = useCallback(() => {
    setPhase((currentPhase) => getNextRulebookPhase(rulebook, currentPhase));
  }, [rulebook]);

  const finishPdf = useCallback(() => {
    if (onPdfComplete) {
      onPdfComplete();
    }

    advancePhase();
  }, [advancePhase, onPdfComplete]);

  useEffect(() => {
    if (phase !== "done" || hasCompleted.current) {
      return;
    }

    hasCompleted.current = true;
    onComplete();
  }, [onComplete, phase]);

  const token = useContext(CachedTokenContext);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const onFileUploadSuccess = async (response) => {
    const newAttachmentId = parseAttachmentIdFromUploadResponse(response);

    if (!newAttachmentId) {
      enqueueSnackbar("An error occurred uploading the rules", { variant: "error" });
      onError();
      return;
    }

    setAttachmentId(newAttachmentId);
    enqueueSnackbar(`${exportName} uploaded`, { variant: "success" });
    await queryClient.invalidateQueries({ queryKey: GameRules(gameId) });
    await queryClient.invalidateQueries({ queryKey: GameById(gameId) });
  };

  const onFileUploadFailure = () => {
    enqueueSnackbar("An error occurred uploading the rules", { variant: "error" });
    onError();
  };

  const { games } = endpoints();
  const standardUpload = useUploadImage(
    s2Url(games().rules(gameId)),
    token,
    "rules",
    onFileUploadSuccess,
    onFileUploadFailure
  );
  const premiumUpload = usePreSignedUploadImage(
    games().rulesStart(gameId),
    games().rulesFinish(gameId),
    token,
    onFileUploadSuccess,
    onFileUploadFailure
  );
  const uploadRules = hasLargeFileUploads ? premiumUpload : standardUpload;

  useEffect(() => {
    if (phase !== "pdf" || hasStarted.current || !rulebook.documentFile || isEntitlementsLoading) {
      return;
    }

    if (shouldSkipImportUpload(rulebook.documentFile, hasLargeFileUploads)) {
      hasStarted.current = true;
      enqueueSnackbar(getImportUploadSkipMessage(rulebook.documentFile), { variant: "warning" });
      onPdfSkip?.();
      advancePhase();
      return;
    }

    if (rules.some((rulesDoc) => rulesDoc.name === exportName)) {
      hasStarted.current = true;
      onStart();
      finishPdf();
      return;
    }

    hasStarted.current = true;
    onStart();

    uploadRules(
      "rules",
      rulebook.documentFile,
      {},
      () => {},
      () => {},
      () => {},
      () => {}
    );
  }, [
    enqueueSnackbar,
    exportName,
    finishPdf,
    hasLargeFileUploads,
    isEntitlementsLoading,
    onPdfSkip,
    onStart,
    phase,
    rulebook,
    rules,
    uploadRules,
  ]);

  if (phase === "pdf" && isEntitlementsLoading) {
    return null;
  }

  if (phase === "pdf") {
    if (!attachmentId) {
      return null;
    }

    return (
      <RenameUploadedRulebook
        gameId={gameId}
        attachmentId={attachmentId}
        name={exportName}
        onComplete={finishPdf}
        onError={onError}
      />
    );
  }

  if (phase === "topic") {
    return (
      <FinalizeImportedRulebook
        gameId={gameId}
        rulebook={rulebook}
        onTopicStart={onTopicStart}
        onTopicComplete={onTopicComplete}
        onTopicError={onTopicError}
        onComplete={advancePhase}
      />
    );
  }

  if (phase === "illustrations") {
    return (
      <UploadRulebookIllustrations
        gameId={gameId}
        rulebook={rulebook}
        onIllustrationStart={onIllustrationStart}
        onIllustrationComplete={onIllustrationComplete}
        onIllustrationSkip={onIllustrationSkip}
        onIllustrationError={onIllustrationError}
        onComplete={advancePhase}
        onError={onError}
      />
    );
  }

  return null;
};

/**
 * @param {object} props
 * @param {import('@seabrookstudios/types').GameId} props.gameId
 * @param {import('../nestifyz/parseExport').NestiFyzRulebook[]} [props.rulebooks]
 * @param {(index: number) => void} props.onItemStart
 * @param {(index: number) => void} props.onItemComplete
 * @param {(index: number) => void} props.onItemSkip
 * @param {(index: number) => void} props.onItemError
 * @param {(index: number) => void} props.onTopicStart
 * @param {(index: number) => void} props.onTopicComplete
 * @param {(index: number) => void} props.onTopicError
 * @param {(rulebookIndex: number, illustrationIndex: number) => void} props.onIllustrationStart
 * @param {(rulebookIndex: number, illustrationIndex: number) => void} props.onIllustrationComplete
 * @param {(rulebookIndex: number, illustrationIndex: number) => void} props.onIllustrationSkip
 * @param {(rulebookIndex: number, illustrationIndex: number) => void} props.onIllustrationError
 * @param {() => void} props.onComplete
 * @param {() => void} props.onError
 */
export const GameImportRulebookUpload = ({
  gameId,
  rulebooks,
  onItemStart,
  onItemComplete,
  onItemSkip,
  onItemError,
  onTopicStart,
  onTopicComplete,
  onTopicError,
  onIllustrationStart,
  onIllustrationComplete,
  onIllustrationSkip,
  onIllustrationError,
  onComplete,
  onError,
}) => {
  const { isLoading: isRulesLoading } = useGameRules(gameId);
  const { isLoading: isTopicsLoading } = useGameTopics(gameId);
  const sortedRulebooks = useMemo(() => getRulebooksForImport(rulebooks), [rulebooks]);
  const [rulebookIndex, setRulebookIndex] = useState(0);
  const currentRulebook = sortedRulebooks[rulebookIndex];

  if (isRulesLoading || isTopicsLoading) {
    return null;
  }

  if (!currentRulebook) {
    return null;
  }

  const advance = () => {
    if (rulebookIndex + 1 >= sortedRulebooks.length) {
      onComplete();
      return;
    }

    setRulebookIndex((index) => index + 1);
  };

  return (
    <UploadRulebook
      key={rulebookIndex}
      gameId={gameId}
      rulebook={currentRulebook}
      onTopicStart={() => {
        onTopicStart(rulebookIndex);
      }}
      onTopicComplete={() => {
        onTopicComplete(rulebookIndex);
      }}
      onTopicError={() => {
        onTopicError(rulebookIndex);
      }}
      onIllustrationStart={(illustrationIndex) => {
        onIllustrationStart(rulebookIndex, illustrationIndex);
      }}
      onIllustrationComplete={(illustrationIndex) => {
        onIllustrationComplete(rulebookIndex, illustrationIndex);
      }}
      onIllustrationSkip={(illustrationIndex) => {
        onIllustrationSkip(rulebookIndex, illustrationIndex);
      }}
      onIllustrationError={(illustrationIndex) => {
        onIllustrationError(rulebookIndex, illustrationIndex);
      }}
      onPdfComplete={
        currentRulebook.documentFile
          ? () => {
              onItemComplete(rulebookIndex);
            }
          : undefined
      }
      onPdfSkip={
        currentRulebook.documentFile
          ? () => {
              onItemSkip(rulebookIndex);
            }
          : undefined
      }
      onStart={() => {
        onItemStart(rulebookIndex);
      }}
      onComplete={advance}
      onError={() => {
        onItemError(rulebookIndex);
        onError();
      }}
    />
  );
};
